import React, { useMemo, useState } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { SignUpFormType } from '../../types/types';
import { emailValidationRegex } from '../../constants/regex';
import { AuthShell, AuthCard } from '../../components/AuthShell';
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  MenuItem,
  Select,
  RadioGroup,
  FormLabel,
  FormControlLabel,
  Radio,
  FormControl,
  Checkbox
} from '@mui/material';
import { useNetwork } from '../../utils/network';
import { track, trackError, trackFormError } from '../../utils/track';
import { useShowSnackbar } from '../../utils/snackbar';
import { validateNameValue } from '../../utils/nameValidation';
import './SignUp.css';
import { publicIp } from 'public-ip';
import { CaptchaModal } from '../../components/CaptchaModal';
import { Country, State } from 'country-state-city';
import { selectMenuProps, selectSx } from '../../components/portalSelect';
import {
  getCountryCallingCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js';

const getCallingCode = (countryIso2: string) => {
  try {
    return countryIso2 ? String(getCountryCallingCode(countryIso2 as any)) : '';
  } catch {
    return '';
  }
};

const validatePhone = (
  localPhone: string,
  countryIso2: string,
  callingCode: string
) => {
  const local = localPhone.replace(/\s/g, '').trim();

  if (!local) return 'Phone number is required.';
  if (!/^\d+$/.test(local)) return 'Phone number can only contain numbers.';

  try {
    const parsed = parsePhoneNumberFromString(
      `+${callingCode}${local}`,
      countryIso2 as any
    );

    if (!parsed) return 'Enter a valid phone number.';
    if (!parsed.isPossible())
      return 'Phone number looks too short or too long.';
    if (!parsed.isValid())
      return 'Enter a valid phone number for your country.';
  } catch {
    return 'Enter a valid phone number.';
  }

  return true;
};

export const SignUp = () => {
  const { post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const { search, state } = useLocation();
  const email = state?.email;
  const params = new URLSearchParams(search);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const countries = useMemo(() => Country.getAllCountries(), []);
  const defaultCountry = useMemo(
    () => countries.find((country) => country.isoCode === 'AU') ?? countries[0],
    [countries]
  );
  const [selectedCountryIso2, setSelectedCountryIso2] = useState<string>(
    defaultCountry?.isoCode ?? ''
  );
  const [selectedStateIso2, setSelectedStateIso2] = useState<string>('');
  const [selectedPhoneCountryCode, setSelectedPhoneCountryCode] =
    useState<string>(getCallingCode(defaultCountry?.isoCode ?? ''));
  const [isGiveawayEligible, setIsGiveawayEligible] = useState<boolean>(false);
  const [hasReadTermsAndConditions, setHasReadTermsAndConditions] =
    useState<boolean>(false);
  const [captchaModalOpen, setCaptchaModalOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<SignUpFormType | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    trigger,
    setError,
    clearErrors
  } = useForm<SignUpFormType>({
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      email: email ?? '',
      phone: '',
      country: {
        code: defaultCountry?.isoCode ?? '',
        label: defaultCountry?.name ?? '',
        phone: selectedPhoneCountryCode
      },
      state: '',
      referralCode: params.get('referralCode') ?? ''
    }
  });

  const states = useMemo(() => {
    if (!selectedCountryIso2) return [];
    return State.getStatesOfCountry(selectedCountryIso2) || [];
  }, [selectedCountryIso2]);

  const handleCountryChange = (countryIso2: string) => {
    const country = countries.find((item) => item.isoCode === countryIso2);
    const callingCode = getCallingCode(countryIso2);

    setSelectedCountryIso2(countryIso2);
    setSelectedStateIso2('');
    setSelectedPhoneCountryCode(callingCode);

    setValue('country', {
      code: countryIso2,
      label: country?.name ?? '',
      phone: callingCode
    });
    setValue('state', '');
    setValue('phone', '');
    clearErrors('phone');
    trigger(['country', 'state', 'phone']);
  };

  const handleStateChange = (stateIso2: string) => {
    const selectedState = states.find((item) => item.isoCode === stateIso2);

    setSelectedStateIso2(stateIso2);
    setValue('state', selectedState?.name ?? '');
    trigger('state');
  };

  const checkSignupAvailability = async (data: SignUpFormType) => {
    const emailToCheck = data.email.trim().toLowerCase();
    const usernameToCheck = data.username.trim();
    const phoneToCheck =
      `+${selectedPhoneCountryCode} ${data.phone.trim()}`.trim();
    let isAvailable = true;

    try {
      const [emailResult, usernameExists, phoneResult] = await Promise.all([
        post('/auth/check-email', { email: emailToCheck }),
        post('/auth/check-username', { username: usernameToCheck }),
        post('/auth/check-phone', {
          phone: phoneToCheck,
          countryCode: selectedPhoneCountryCode
        })
      ]);

      if (emailResult?.exists) {
        trackFormError('signup', ['email'], { reason: 'email_taken' });
        setError('email', {
          type: 'manual',
          message: 'This email is already registered.'
        });
        isAvailable = false;
      }

      if (usernameExists) {
        trackFormError('signup', ['username'], { reason: 'username_taken' });
        setError('username', {
          type: 'manual',
          message: 'Username is not available.'
        });
        isAvailable = false;
      }

      if (phoneResult?.exists) {
        trackFormError('signup', ['phone'], { reason: 'phone_taken' });
        setError('phone', {
          type: 'manual',
          message: 'This phone number is already linked to another account.'
        });
        isAvailable = false;
      }
    } catch (error: any) {
      showSnackbar(
        error?.message || 'Unable to check account details. Please try again.',
        'error'
      );
      isAvailable = false;
    }

    return isAvailable;
  };

  const submitSignup = async (
    formData: SignUpFormType,
    captchaToken: string
  ) => {
    setIsSubmitting(true);

    try {
      await post('/auth/sign-up', {
        // the "I have read and agree" box — the form does not submit without it; the server records it
        termsAccepted: hasReadTermsAndConditions,
        username: formData.username,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: `+${selectedPhoneCountryCode} ${formData.phone.trim()}`,
        country: formData.country.label,
        state: formData.state,
        referralCode: formData.referralCode,
        inviteCode: params.get('inviteCode'),
        ipAddress: await publicIp(),
        isGiveawayEligible,
        signupSource: 'sign-up',
        captchaToken
      });
      // First-party acquisition event (flags + source only — no PII).
      track('auth.signup', {
        source: 'sign-up',
        hasReferral: Boolean(formData.referralCode),
        hasInvite: Boolean(params.get('inviteCode')),
        country: formData.country.label,
        eligible: isGiveawayEligible
      });
      setIsEmailSent(true);
      setIsSubmitting(false);
      setSelectedCountryIso2(defaultCountry?.isoCode ?? '');
      setSelectedStateIso2('');
      setSelectedPhoneCountryCode(
        getCallingCode(defaultCountry?.isoCode ?? '')
      );
      reset({
        firstName: '',
        lastName: '',
        username: '',
        email: email ?? '',
        phone: '',
        country: {
          code: defaultCountry?.isoCode ?? '',
          label: defaultCountry?.name ?? '',
          phone: getCallingCode(defaultCountry?.isoCode ?? '')
        },
        state: '',
        referralCode: params.get('referralCode') ?? ''
      });
      setCaptchaModalOpen(false);
      setPendingFormData(null);
      showSnackbar(
        'Account created successfully! Please check your email for verification.',
        'success'
      );
    } catch (error: any) {
      trackError('auth.signup', error, { source: 'sign-up' });
      showSnackbar(
        error?.message ||
          error?.response?.data?.message ||
          'Unable to create account.',
        'error'
      );
      setIsSubmitting(false);
      setCaptchaModalOpen(false);
    }
  };

  const onSubmit: SubmitHandler<SignUpFormType> = async (data) => {
    clearErrors(['email', 'username', 'phone']);

    const isAvailable = await checkSignupAvailability(data);

    if (!isAvailable) return;

    const trimmedFormData = {
      ...data,
      email: data.email.trim().toLowerCase(),
      username: data.username.trim()
    };

    setPendingFormData(trimmedFormData);
    setCaptchaModalOpen(true);
  };

  const handleCaptchaSuccess = async (captchaToken: string) => {
    if (!pendingFormData) return;

    await submitSignup(pendingFormData, captchaToken);
  };

  const handleCaptchaClose = () => {
    setCaptchaModalOpen(false);
    setPendingFormData(null);
  };

  return (
    <AuthShell maxWidth={520}>
      <AuthCard>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Typography component="h1" variant="h4">
            Sign up
          </Typography>
          {!isEmailSent ? (
            <>
              <Box sx={{ mt: 1 }}>
                <form
                  onSubmit={handleSubmit(onSubmit, (errs) =>
                    trackFormError('signup', Object.keys(errs))
                  )}
                >
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: true, validate: validateNameValue }}
                    render={({ field }) => (
                      <TextField
                        id="firstName"
                        margin="normal"
                        required
                        fullWidth
                        label="First Name"
                        error={Boolean(errors.firstName)}
                        helperText={
                          errors.firstName &&
                          ((errors.firstName.message as string) ||
                            'Please enter your first name')
                        }
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: true, validate: validateNameValue }}
                    render={({ field }) => (
                      <TextField
                        id="lastName"
                        margin="normal"
                        required
                        fullWidth
                        label="Last Name"
                        error={Boolean(errors.lastName)}
                        helperText={
                          errors.lastName &&
                          ((errors.lastName.message as string) ||
                            'Please enter your last name')
                        }
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name="username"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        id="username"
                        margin="normal"
                        required
                        fullWidth
                        label="Username"
                        error={Boolean(errors.username)}
                        helperText={
                          errors.username?.message ||
                          (errors.username && 'Please enter a username')
                        }
                        {...field}
                        onChange={(event) => {
                          clearErrors('username');
                          field.onChange(event);
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: true,
                      pattern: emailValidationRegex
                    }}
                    render={({ field }) => (
                      <TextField
                        id="email"
                        margin="normal"
                        required
                        fullWidth
                        label="Email"
                        autoComplete="email"
                        error={Boolean(errors.email)}
                        helperText={
                          errors.email?.message ||
                          (errors.email && 'Please enter a valid email')
                        }
                        {...field}
                        value={email || field.value}
                        onChange={(event) => {
                          clearErrors('email');
                          field.onChange(event);
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="country"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <FormControl
                        margin="normal"
                        required
                        fullWidth
                        error={Boolean(errors.country)}
                      >
                        <FormLabel sx={{ mb: 1 }}>Country</FormLabel>
                        <Select
                          sx={selectSx}
                          MenuProps={selectMenuProps}
                          value={selectedCountryIso2}
                          onChange={(event) => {
                            handleCountryChange(String(event.target.value));
                            field.onChange({
                              code: String(event.target.value),
                              label:
                                countries.find(
                                  (country) =>
                                    country.isoCode === event.target.value
                                )?.name ?? '',
                              phone: getCallingCode(String(event.target.value))
                            });
                          }}
                          displayEmpty
                        >
                          {countries.map((country) => (
                            <MenuItem
                              key={country.isoCode}
                              value={country.isoCode}
                            >
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.country && (
                          <Typography
                            sx={{
                              mt: 0.5,
                              color: 'error.main',
                              fontSize: '0.75rem'
                            }}
                          >
                            Please select your country
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="state"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <FormControl
                        margin="normal"
                        required
                        fullWidth
                        error={Boolean(errors.state)}
                      >
                        <FormLabel sx={{ mb: 1 }}>State/Province</FormLabel>
                        <Select
                          sx={selectSx}
                          MenuProps={selectMenuProps}
                          value={selectedStateIso2}
                          onChange={(event) => {
                            handleStateChange(String(event.target.value));
                            field.onChange(
                              states.find(
                                (item) => item.isoCode === event.target.value
                              )?.name ?? ''
                            );
                          }}
                          onBlur={field.onBlur}
                          disabled={states.length === 0}
                          displayEmpty
                        >
                          {states.length === 0 ? (
                            <MenuItem value="" disabled>
                              No states available
                            </MenuItem>
                          ) : (
                            states.map((state) => (
                              <MenuItem
                                key={state.isoCode}
                                value={state.isoCode}
                              >
                                {state.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {errors.state && (
                          <Typography
                            sx={{
                              mt: 0.5,
                              color: 'error.main',
                              fontSize: '0.75rem'
                            }}
                          >
                            Please select your state/province
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="phone"
                    control={control}
                    rules={{
                      required: true,
                      validate: (value) =>
                        validatePhone(
                          value,
                          selectedCountryIso2,
                          selectedPhoneCountryCode
                        )
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="phone"
                        margin="normal"
                        required
                        fullWidth
                        label="Phone number"
                        error={Boolean(errors.phone)}
                        helperText={errors.phone && errors.phone.message}
                        onChange={(event) => {
                          clearErrors('phone');
                          field.onChange(
                            event.target.value.replace(/[^\d\s]/g, '')
                          );
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ fontWeight: 800 }}>
                                +{selectedPhoneCountryCode}
                              </Typography>
                            </InputAdornment>
                          )
                        }}
                        inputProps={{
                          inputMode: 'tel',
                          autoComplete: 'tel-national'
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="referralCode"
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <TextField
                        margin="normal"
                        fullWidth
                        label="Referral Code"
                        id="referralCode"
                        {...field}
                      />
                    )}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={() =>
                          setHasReadTermsAndConditions(
                            !hasReadTermsAndConditions
                          )
                        }
                      />
                    }
                    label={
                      <Typography>
                        I have read and agree to the{' '}
                        <Link
                          href="https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/documents/terms_and_conditions_05_08_2026.docx"
                          rel="noopener noreferrer"
                          variant="body2"
                          target="_blank"
                          style={{ fontSize: '16px' }}
                        >
                          terms and conditions
                        </Link>
                      </Typography>
                    }
                    labelPlacement="end"
                  />
                  <FormControl sx={{ mt: '1rem' }}>
                    <FormLabel id="giveaway-Eligibility">
                      You have read the T&Cs and are eligible for giveaways
                    </FormLabel>
                    <RadioGroup
                      onChange={(e) =>
                        setIsGiveawayEligible(e.target.value === 'yes')
                      }
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio required={true} />}
                        label="Yes I am eligible for giveaways"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio required={true} />}
                        label="No I am not eligible for giveaways"
                      />
                    </RadioGroup>
                  </FormControl>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    disabled={!hasReadTermsAndConditions || isSubmitting}
                  >
                    Sign Up
                  </Button>
                  <Grid container>
                    <Grid item>
                      <Link
                        component={ReactRouterLink}
                        to="/login"
                        variant="body2"
                      >
                        {'Already have an account? Login'}
                      </Link>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ mt: '2rem' }}>
                <Typography variant="body1">
                  User created successfully. Please verify your email.
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <CaptchaModal
          open={captchaModalOpen}
          onClose={handleCaptchaClose}
          onSuccess={handleCaptchaSuccess}
          title="Verify to Create Account"
          description="Complete the verification to create your account"
        />
      </AuthCard>
    </AuthShell>
  );
};
